<!-- 
  /** 
  * @author Mau de Pallares 
  * @company Erillam Healthcare 
  * @version 2.0 
  */ -->
<template>
  <div>
    <loader :loader="sending" />
    <v-data-table
      :headers="headers"
      :items="list"
      :items-per-page="itemsPerPage"
      sort-by="startTime"
      :sort-desc="true"
      no-data-text="Sin resultados disponibles"
      dense
      fixed-header
      hide-default-footer
    >
      <template v-slot:[`item.actions`]="{ item }">
        <v-tooltip
          top
          v-if="$utilFront.withPermission($prm.MODULE_OPTOMETRY_VIEW)"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-btn icon small @click="isViewDetailsTable(item.idMedicalPractice)">
              <v-icon color="blue" v-bind="attrs" v-on="on">
                mdi-information-variant
              </v-icon>
            </v-btn>
          </template>
          <span>Ver información de consulta</span>
        </v-tooltip>

        <v-menu offset-y :nudge-width="200" :close-on-content-click="true">
          <template v-slot:activator="{ on: menu, attrs }">
            <v-tooltip top>
              <template v-slot:activator="{ on: tooltip }">
                <v-btn icon small v-bind="attrs" v-on="{ ...tooltip, ...menu }">
                  <v-icon>mdi-dots-vertical</v-icon>
                </v-btn>
              </template>
              <span>Más opciones</span>
            </v-tooltip>
          </template>
          <v-list dense>
            <v-list-item
              link
              @click="isEditableTable(item.idMedicalPractice)"
              v-if="$utilFront.withPermission($prm.MODULE_OPTOMETRY_EDIT)"
            >
              <v-list-item-icon>
                <v-icon small color="warning" size="20px"> mdi-pencil </v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title> 
                  <small>Editar consulta</small>
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>

            <v-list-item
              link
              @click="
                generatePDF(
                  item.idMedicalPractice,
                  item.startTime,
                  item.idPatient
                )
              "
              v-if="
                $utilFront.withPermission($prm.MODULE_OPTOMETRY_DOWNLOADPDF)
              "
            >
              <v-list-item-icon>
                <v-icon small color="error" size="20px"> mdi-file-pdf-box </v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title>
                  <small>Consulta optométrica</small>
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </v-menu>
      </template>
      <template v-slot:[`item.startTime`]="{ item }">
        {{ $utilFront.formatDateTime(item.startTime) }}
      </template>
    </v-data-table>
    <v-card-actions align="center">
      <v-col
        v-if="$utilFront.withPermission($prm.MODULE_OPTOMETRY_EXPORTEXCEL)"
      >
        <v-btn
          outlined
          small
          color="green darken-1"
          :disabled="sending"
          @click="downloadReportXLSX()"
        >
          {{ sending ? "Exportando..." : "Exportar consultas" }}
          <v-icon right>mdi-microsoft-excel</v-icon>
        </v-btn>
      </v-col>
      <v-col
        v-if="$utilFront.withPermission($prm.MODULE_OPTOMETRY_PRODUCTIVITY)"
      >
        <v-btn
          outlined
          small
          color="green darken-1"
          :disabled="sending"
          @click="dialogProductivity = true"
        >
          {{ sending ? "Descargando..." : "Descargar productividad" }}
          <v-icon right>mdi-microsoft-excel</v-icon>
        </v-btn>
      </v-col>
    </v-card-actions>
    <v-dialog v-model="dialogProductivity" max-width="600">
      <v-card>
        <v-card-title class="text-h6 text-center">
          Descargar productividad
          <small>
            Descarga de lista de pacientes atendidos por día.
          </small>
        </v-card-title>
        <validation-observer ref="observer" v-slot="{ invalid }">
          <v-card-text>
            <v-row align="center" justify="center" class="text-center">
              <v-col>
                <validation-provider
                  v-slot="{ errors }"
                  name="Fecha"
                  rules="required"
                >
                  <vc-date-picker
                    :max-date="new Date()"
                    color="green"
                    v-model="dateProductivity"
                    is-required
                    required
                    :error-messages="errors"
                  />
                </validation-provider>
              </v-col>
            </v-row>
          </v-card-text>

          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn
              color="error"
              text
              :disabled="sending"
              @click="dialogProductivity = false"
            >
              Cancelar
            </v-btn>
            <v-btn
              color="primary"
              text
              @click="downloadProductivityXLSX()"
              :disabled="invalid || sending"
            >
              Aceptar
            </v-btn>
          </v-card-actions>
        </validation-observer>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import MenuCrud from "@/app/components/base/menu/MenuCrud.vue";
import ScheduleAppointmentDialog from "@/app/components/reception/ScheduleAppointmentDialog.vue";
import { OptometryHistoryService } from "@/app/services";
import moment from "moment";
import Loader from "@/app/components/base/loader/Loader.vue";
import { ValidationObserver, ValidationProvider } from "vee-validate";

export default {
  name: "PsychologyHistorialTable",
  props: {
    list: Array,
    get: Function,
    deleteItem: Function,
    updateStatus: Function,
    itemsPerPage: Number,
    doAction: Function,
    range: Object,
    idBranchOffice: String,
    search: String,
  },
  components: {
    MenuCrud,
    ScheduleAppointmentDialog,
    Loader,
    ValidationObserver,
    ValidationProvider,
  },
  data() {
    return {
      sending: false,
      idPatient: null,
      dialogProductivity: false,
      dataModal: {},
      dateProductivity: new Date(),
      headers: [
        {
          text: "Id Consulta",
          value: "idMedicalPractice",
          sortable: false,
          align: "center",
          width: "10%",
        },
        {
          text: "Id Paciente",
          value: "idPatient",
          sortable: false,
          align: "center",
          width: "10%",
        },
        {
          text: "Nombre del paciente",
          value: "fullName",
          align: "center",
          sortable: false,
          width: "20%",
        },
        {
          text: "Nombre del doctor",
          value: "fullNameDoctor",
          align: "center",
          sortable: false,
          width: "20%",
        },
        {
          text: "Fecha y hora de atención",
          value: "startTime",
          align: "center",
          sortable: false,
          width: "15%",
        },
        {
          text: "Acciones",
          value: "actions",
          sortable: false,
          align: "center",
          width: "15%",
        },
      ],
    };
  },
  methods: {
    // openDialogCreateAppointment(idPatient) {
    //   this.idPatient = idPatient;
    //   this.appointmentDialog = true;
    // },
    // closeDialogCreateAppointment() {
    //   this.idPatient = null;
    //   this.appointmentDialog = false;
    // },
    generatePDF(idMedicalPractice, time, idPatient) {
      this.sending = true;
      if (idMedicalPractice) {
        const fileName = `Consulta_Optometría_${idMedicalPractice}_${moment(
          new Date(time)
        ).format("YYYY-MM-DD")}`;
        const data = {
          idMedicalPractice: idMedicalPractice,
          fileName: fileName,
          idBranchOffice: this.$utilFront.getIdBranchOffice(),
          idPatient: idPatient,
        };
        OptometryHistoryService.pdf(data)
          .then((res) => {
            if (res.status == 200 && res.data.type != "application/json") {
              this.$utilFront.openPDF(res.data);
            } else {
              this.$utilFront.displayErrorPdf(res.data);
            }
          })
          .finally(() => {
            //change action to table
            this.sending = false;
          });
      }
    },
    isViewDetailsTable(id) {
      let isViewDetails = true;
      this.$emit("isViewDetails", isViewDetails);
      this.get(id, 3);
    },
    isEditableTable(id) {
      let isViewDetails = false;
      this.$emit("isViewDetails", isViewDetails);
      this.get(id, 3);
    },
    downloadReportXLSX() {
      if (!this.sending) {
        this.sending = true;

        let data = {
          search: this.search,
          idBranchOffice: this.$utilFront.nonEmpty(
            this.idBranchOffice,
            this.$utilFront.getIdBranchOffice()
          ),
        };

        let rangeName = "";

        if (this.$utilFront.nonEmpty(this.range)) {
          data.start = this.$utilFront.startOfDay(this.range.start);
          data.end = this.$utilFront.endOfDay(this.range.end);
          rangeName = ` del ${this.$utilFront.formatDate(
            data.start
          )} al ${this.$utilFront.formatDate(data.end)}`;
        }

        let fileName = `Historial de consultas - Optometría${rangeName}.xlsx`;

        OptometryHistoryService.excel(data)
          .then((res) => {
            let { data } = res;
            this.$utilFront.downloadXLSX(data, fileName);
          })
          .catch((error) => {
            this.$utilFront.errorhttp(error);
          })
          .finally(() => {
            this.sending = false;
          });
      }
    },
    downloadProductivityXLSX() {
      if (!this.sending) {
        this.sending = true;
        let data = {
          startDate: this.$utilFront.startOfDay(this.dateProductivity),
          finishDate: this.$utilFront.endOfDay(this.dateProductivity),
          idBranchOffice: this.$utilFront.nonEmpty(
            this.idBranchOffice,
            this.$utilFront.getIdBranchOffice()
          ),
          idHumanResource: this.$utilFront.getIdHumanResource(),
          speciality: 5,
          statusMedicalPractice: 3,
        };
        let fileName = `Productividad - Optometría${moment(
          new Date(data.startDate)
        ).format("YYYY-MM-DD")}.xlsx`;

        OptometryHistoryService.productivity(data)
          .then((res) => {
            let { data } = res;
            this.$utilFront.downloadXLSX(data, fileName);
            this.dialogProductivity = false;
          })
          .catch((error) => {
            this.$utilFront.errorhttp(error);
          })
          .finally(() => {
            this.sending = false;
          });
      }
    },
  },
};
</script>
